import { isAnalyticsEventName, type AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import type { CustomerEmitEventInput } from '../analytics/customerAnalyticsClient.js';
import { emitCustomerErrorShown } from '../analytics/customerErrorAnalytics.js';
import type { RecoveryDefinition } from './types.js';

export type RecoveryAnalyticsTrack = (input: CustomerEmitEventInput) => void;

export function recoveryAnalyticsScreenName(msId: string): string {
  return msId.toLowerCase().replace(/-/g, '_');
}

/**
 * Emit catalog-backed recovery telemetry.
 *
 * Registry rows may list blueprint aliases (`recovery_*_shown`, `recovery_primary_cta`)
 * that are not ingestable yet — only `error_shown` / other catalog names are POSTed.
 * CTA clicks map to `cta_clicked` (strict metadata: element_id / cta_label).
 */
export function emitRecoveryShown(
  definition: RecoveryDefinition,
  track: RecoveryAnalyticsTrack,
  metadata?: Record<string, string | number | boolean | null>,
): void {
  const screenName = recoveryAnalyticsScreenName(definition.msId);

  emitCustomerErrorShown({
    track,
    screenName,
    failureReason: definition.msId,
    operation: definition.classSlug,
    message: definition.trigger,
    recovery_class: definition.classSlug,
    recovery_ms_id: definition.msId,
  });

  for (const eventName of definition.analyticsEvents) {
    if (eventName === 'error_shown') {
      continue;
    }
    if (!isAnalyticsEventName(eventName)) {
      // Blueprint alias not in ANALYTICS_EVENT_NAMES / backend catalog — skip to avoid 422.
      continue;
    }
    track({
      eventName,
      screenName,
      metadata: {
        recovery_ms_id: definition.msId,
        recovery_class: definition.classSlug,
        ...metadata,
      },
    });
  }
}

export function emitRecoveryPrimaryCta(
  definition: RecoveryDefinition,
  track: RecoveryAnalyticsTrack,
  ctaKind: string,
): void {
  track({
    eventName: 'cta_clicked' as AnalyticsEventName,
    screenName: recoveryAnalyticsScreenName(definition.msId),
    metadata: {
      element_id: `recovery_${definition.classSlug}_${ctaKind}`,
      cta_label: ctaKind,
    },
  });
}
